import { AlertTriangle, ArrowUpCircle, Clock, Frown, Info } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';

interface ErrorConfig {
  title: string;
  message: string | null;
  canRetry: boolean;
}

interface ErrorInfo {
  type?: string;
  message?: string;
  retryAfter?: string | number;
}

interface ErrorModalProps {
  error: ErrorInfo | null;
  onDismiss: () => void;
  onRetry?: () => Promise<void>;
  /** Optional upgrade handler, shown as a CTA for quota-exceeded and plan-limit errors */
  onUpgrade?: () => void;
}

const ERROR_CONFIGS: Record<string, ErrorConfig> = {
  overloaded_error: {
    title: 'AI Service Busy',
    message: 'The AI is experiencing high demand. Please try again in a moment.',
    canRetry: true,
  },
  rate_limit_error: {
    title: 'Too Many Requests',
    message: null, // Use server-provided message (burst limit vs daily quota have different messages)
    canRetry: true,
  },
  api_error: {
    title: 'Service Unavailable',
    message: 'Unable to connect to the AI service. Please try again.',
    canRetry: true,
  },
  generation_in_progress: {
    title: 'Generation Already Running',
    message: null, // Use server-provided message
    canRetry: true,
  },
  authentication_error: {
    title: 'Configuration Error',
    message: "There's an issue with the API configuration. Please contact support.",
    canRetry: false,
  },
  props_unavailable: {
    title: 'Props Not Available Yet',
    message: null, // Will use the server-provided message with timing details
    canRetry: false,
  },
  no_picks: {
    title: 'No Picks Generated',
    message:
      "The AI couldn't find any high-confidence picks for your selections. This can happen when lines aren't available yet for the selected games, lines are unfavorable, or there's insufficient statistical edge. Try selecting different games, adjusting your prop types, or using Force Refresh to generate new picks.",
    canRetry: true,
  },
  load_error: {
    title: 'Failed to Load Data',
    message: null, // Will use the actual error message
    canRetry: true,
  },
  tier_limit: {
    title: 'Not Included in Your Plan',
    message: null, // Server names the factor limit or the plan that unlocks it
    canRetry: false,
  },
  track_quota_exceeded: {
    title: 'Daily Tracking Limit Reached',
    message: null, // Uses dynamic message with quota details + reset time
    canRetry: false,
  },
  // A settings change that did not save; the picker's Retry would regenerate picks instead
  save_error: {
    title: "Couldn't Save Your Change",
    message: null,
    canRetry: false,
  },
  generic: {
    title: 'Something Went Wrong',
    message: null, // Will use the actual error message
    canRetry: true,
  },
};

export default function ErrorModal({
  error,
  onDismiss,
  onRetry,
  onUpgrade,
}: ErrorModalProps): ReactElement | null {
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const errorType = error?.type || 'generic';
  const config = ERROR_CONFIGS[errorType] || ERROR_CONFIGS.generic;
  const retryAfter = error?.retryAfter ? parseInt(String(error.retryAfter), 10) : null;

  useEffect(() => {
    if (retryAfter && retryAfter > 0) {
      setCountdown(retryAfter);
    }
  }, [retryAfter]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setTimeout(() => {
      setCountdown((c) => (c !== null ? c - 1 : null));
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  const handleRetry = async (): Promise<void> => {
    if (countdown !== null && countdown > 0) return;
    setIsRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setIsRetrying(false);
    }
  };

  if (!error) return null;

  const displayMessage = config.message || error.message || 'An unexpected error occurred.';
  const canRetryNow = config.canRetry && (countdown === null || countdown <= 0);
  const showUpgrade =
    (errorType === 'track_quota_exceeded' || errorType === 'tier_limit') && onUpgrade;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - no click to dismiss, user must use button */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              errorType === 'props_unavailable'
                ? 'bg-theme-primary/50'
                : errorType === 'no_picks'
                  ? 'bg-theme-surface-hover'
                  : errorType === 'overloaded_error'
                    ? 'bg-theme-warning/50'
                    : errorType === 'rate_limit_error' || errorType === 'track_quota_exceeded'
                      ? 'bg-orange-500/50'
                      : 'bg-theme-danger/50'
            }`}
          >
            {errorType === 'props_unavailable' ? (
              <Info className="w-8 h-8 text-theme-primary" />
            ) : errorType === 'no_picks' ? (
              <Frown className="w-8 h-8 text-theme-text-secondary" />
            ) : errorType === 'overloaded_error' ? (
              <Info className="w-8 h-8 text-theme-warning" />
            ) : errorType === 'rate_limit_error' || errorType === 'track_quota_exceeded' ? (
              <Clock className="w-8 h-8 text-orange-400" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-theme-danger" />
            )}
          </div>
        </div>

        {/* Title */}
        <h3
          className={`text-xl font-bold text-center mb-2 ${
            errorType === 'props_unavailable'
              ? 'text-theme-primary'
              : errorType === 'no_picks'
                ? 'text-theme-text'
                : errorType === 'overloaded_error'
                  ? 'text-theme-warning'
                  : errorType === 'rate_limit_error' || errorType === 'track_quota_exceeded'
                    ? 'text-orange-400'
                    : 'text-theme-danger'
          }`}
        >
          {config.title}
        </h3>

        {/* Message */}
        <p className="text-theme-text text-center mb-6">{displayMessage}</p>

        {/* Countdown */}
        {countdown !== null && countdown > 0 && (
          <div className="text-center mb-4">
            <span className="text-theme-text-secondary text-sm">Retry available in </span>
            <span className="text-theme-text font-mono font-bold">{countdown}s</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="flex-1 py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text font-medium transition"
          >
            Dismiss
          </button>
          {showUpgrade && (
            <button
              type="button"
              onClick={() => {
                onDismiss();
                onUpgrade();
              }}
              className="flex-1 py-2.5 rounded-lg bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium transition flex items-center justify-center gap-2"
            >
              <ArrowUpCircle className="w-4 h-4" />
              {errorType === 'tier_limit' ? 'See plans' : 'Upgrade'}
            </button>
          )}
          {config.canRetry && onRetry && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={!canRetryNow || isRetrying}
              className={`flex-1 py-2.5 rounded-lg font-medium transition ${
                canRetryNow && !isRetrying
                  ? 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-tertiary cursor-not-allowed'
              }`}
            >
              {isRetrying ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-theme-text-inverse border-t-transparent"></span>
                  Retrying...
                </span>
              ) : countdown !== null && countdown > 0 ? (
                `Wait ${countdown}s`
              ) : (
                'Try Again'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
